import { Injectable } from '@angular/core';
import { BottomPanelTabs, RightPanelTabs } from './locations-skeleton.component';
import { LocalStorageKeys } from './enums/local-storage-keys';
import { FeedbackService } from '../../core/feedback.service';
import { combineLatest, Observable, of } from 'rxjs';
import { ConfirmationDialogType } from '../global/confirmation-popup/confirmation-popup.component';
import { getFromLocalStorage, setToLocalStorage } from '../../core/utilities';
import { Store } from '@ngrx/store';
import * as skeletonSelectors from './state/locations-skeleton-selectors';
import { switchMap, take } from 'rxjs/operators';

export const RIGHT_PANEL_WIDTH_MIN_PX = 462; // enough so buttons don't overflow
export const MAIN_PANEL_WIDTH_MIN_PX = 516; // store code + status columns
export const BOTTOM_PANEL_HEIGHT_DEFAULT_PERC = 50;
export const VERTICAL_HANDLE_HEIGHT = 8;

export interface ContainerDimensions {
  topPanelRelativeHeight: number;
  rightPanelRelativeWidth: number;
}

@Injectable()
export class LocationsSkeletonService {

  constructor(
      private feedbackService: FeedbackService,
      private store: Store) {
  }

  /**
   * Calculates bottom panel height relative to the total container height (in %)
   * @param bottomPanelHeight
   * @param totalHeight
   */
  calculateBottomPanelRelativeHeight(bottomPanelHeight: number, totalHeight: number): number {
    return (bottomPanelHeight / totalHeight) * 100;
  }

  /**
   * Calculates right panel width relative to the total container width (in %)
   * @param rightPanelWidth
   * @param totalWidth
   */
  calculateRightPanelRelativeWidth(rightPanelWidth: number, totalWidth: number): number {
    return (rightPanelWidth / totalWidth) * 100;
  }

  /**
   * Calculates right panel width (in px)
   * @param rightPanelRelativeWidth relative width (in %)
   */
  calculateRightPanelWidth(rightPanelRelativeWidth: number): number {
    return ((window as any).innerWidth / 100) * rightPanelRelativeWidth;
  }

  /**
   * Save container dimensions to local storage
   * @param bottomPanelRelativeHeight
   * @param rightPanelRelativeWidth
   */
  saveContainerDimensionsToLocalStorage(bottomPanelRelativeHeight: number, rightPanelRelativeWidth: number) {
    const payload: ContainerDimensions = {
      topPanelRelativeHeight: bottomPanelRelativeHeight,
      rightPanelRelativeWidth: rightPanelRelativeWidth,
    };
    setToLocalStorage(LocalStorageKeys.LOCATIONS_CONTAINER_DIMENSIONS_KEY, payload);
  }

  /**
   * Get container dimensions from local storage
   */
  getContainerDimensionsFromLocalStorage(): ContainerDimensions | undefined {
    const payload: ContainerDimensions = getFromLocalStorage(
        LocalStorageKeys.LOCATIONS_CONTAINER_DIMENSIONS_KEY);
    if (!(payload && typeof payload === 'object')) {
      console.error(`missing value or invalid type of ${LocalStorageKeys.LOCATIONS_CONTAINER_DIMENSIONS_KEY}: ${payload}`);
      return undefined;
    }

    return payload;
  }

  /**
   * Will prevent store update if the proposed height doesn't pass the
   * check
   * @param bottomPanelHeight
   * @param totalHeight
   */
  validateBottomPanelHeight(bottomPanelHeight: number, totalHeight: number): boolean {
    // don't let the handle disappear - will not allow a height that would push the handle off
    // viewport
    return bottomPanelHeight <= totalHeight && bottomPanelHeight >= VERTICAL_HANDLE_HEIGHT;
  }

  /**
   * Will prevent store update if the proposed height doesn't pass the
   * check
   * @param rightPanelWidth
   * @param totalWidth
   */
  validateRightPanelWidth(rightPanelWidth: number, totalWidth: number): boolean {
    const SIDE_PANEL_MAX_PX = (totalWidth - MAIN_PANEL_WIDTH_MIN_PX);
    return rightPanelWidth >= RIGHT_PANEL_WIDTH_MIN_PX && rightPanelWidth <= SIDE_PANEL_MAX_PX;
  }

  /**
   * Will only return values that pass the check.
   * Since we're storing right panel's relative width to container total width, it can happen
   * that saving right panel's width on a big screen and restoring it on a small screen will result
   * in a right panel width smaller than the minimum allowed width, or bigger than the max allowed
   * width in which case width should be reset to default
   * @param totalWidth
   */
  validateStoredRightPanelWidth(totalWidth: number): number | false {
    const payload: ContainerDimensions = this.getContainerDimensionsFromLocalStorage();

    if (!payload || !payload.rightPanelRelativeWidth) {
      console.warn('Setting default right panel width - value missing');
      return false;
    }

    const rightPanelWidth = totalWidth / 100 * payload.rightPanelRelativeWidth;
    return this.validateRightPanelWidth(rightPanelWidth, totalWidth) ? payload.rightPanelRelativeWidth : false;
  };

  /**
   * Will only return values that pass the check.
   * @param totalHeight
   */
  validateStoredBottomPanelHeight(totalHeight: number): number | false {
    const payload: ContainerDimensions = this.getContainerDimensionsFromLocalStorage();

    if (!payload || !payload.topPanelRelativeHeight) {
      console.warn('Setting default bottom panel height - value missing');
      return false;
    }

    const topPanelHeight = totalHeight / 100 * payload.topPanelRelativeHeight;
    return this.validateBottomPanelHeight(topPanelHeight, totalHeight) ?
        payload.topPanelRelativeHeight : false;
  };

  /**
   * Return registered svg name based on tab name
   * @param tab
   */
  getSvgName(tab: RightPanelTabs | BottomPanelTabs): string {
    switch (tab) {
      case RightPanelTabs.MAP:
        return 'map_outlined';
      case RightPanelTabs.EDIT:
        return 'edit_location_outlined';
      case BottomPanelTabs.CC_RESULTS:
        return 'cc_results';
      case BottomPanelTabs.COMPARE:
        return 'compare_arrows';
      case BottomPanelTabs.UPDATES:
        return 'update';
      case BottomPanelTabs.VALIDATION_HISTORY:
        return 'validation_history';
      default:
        return '';
    }
  }

  /**
   * Shows confirmation dialog. Should be triggered if there are unsaved changes in the right- or
   * bottom-panel tab(s) (currently applies only to BLS/GMB differences and Edit-location) when a
   * user action would cause the tab to be closed/updated.
   */
  showPanelClosingConfirmDialog$(panel: 'right' | 'bottom'): Observable<boolean> {
    return this.feedbackService.showConfirmationPopup({
      data: {
        title: `Unsaved changes in ${panel} panel tab`,
        text: `There are unsaved changes in the currently active ${panel} panel tab. ` +
            'Continuing will discard these changes. Would you like to continue?',
        submitButtonText: 'Continue',
        cancelButtonText: 'Cancel',
        confirmationDialogType: ConfirmationDialogType.INFO,
      },
    });
  }

  /**
   * If there are unsaved changes in any right panel tab, we need to ask for user permission before
   * we can allow an action that would destroy the instantiated right panel container
   */
  canDestroyRightPanelContainer$(): Observable<boolean> {
    return this.store.select(skeletonSelectors.selectPermissionToDestroyRightPanelTabContainerRequired)
        .pipe(
            switchMap((userPermissionRequired) => userPermissionRequired ?
                this.showPanelClosingConfirmDialog$('right') : of(true)),
            take(1),
        );
  }

  /**
   * If there are unsaved changes in any bottom panel tab, we need to ask for user permission before
   * we can allow an action that would destroy the instantiated bottom panel container
   */
  canDestroyBottomPanelContainer$(): Observable<boolean> {
    return this.store.select(skeletonSelectors.selectPermissionToDestroyBottomPanelTabContainerRequired)
        .pipe(
            switchMap((userPermissionRequired) => userPermissionRequired ?
                this.showPanelClosingConfirmDialog$('bottom') : of(true)),
            take(1),
        );
  }

  /**
   * If there are unsaved changes in any bottom or top panel tab, we need to ask for user permission
   * before we can allow an action that would destroy the instantiated bottom or top panel container
   */
  canDestroyExpansionPanelContainers$(): Observable<boolean> {
    return combineLatest([
      this.canDestroyRightPanelContainer$(),
      this.canDestroyBottomPanelContainer$(),
    ], (canDestroyRightPanelContainer, canDestroyBottomPanelContainer) =>
        canDestroyRightPanelContainer && canDestroyBottomPanelContainer);
  }


}
